<template>
  <div>
    <v-card class="card">
      <v-tabs v-model="tab" class="align-center" density="default" height="64">
        <v-tab ripple value="list-subscriptions">Subscriptions</v-tab>
        <v-spacer></v-spacer>
        <v-text-field
          class="align-self-right mt-0 pt-0"
          append-inner-icon="mdi-magnify"
          v-model="search"
          label="Search"
          :single-line="true"
          hide-details
          variant="outlined"
          density="compact"
          v-if="tab === 'list-subscriptions'"
        >
        </v-text-field>
      </v-tabs>

      <v-window v-if="tab === 'list-subscriptions'">
        <v-data-table
          :loading="loadingSubscriptions"
          v-if="subscriptions"
          :headers="headersSubscriptions"
          :items="filteredSubscriptions"
          hover
          v-model:expanded="expanded"
          show-expand
          v-model:sort-by="sortBy"
        >
          <template v-slot:item.payerUser.email="{ item }">
            <div>
              <v-tooltip v-if="item.payerUser?.id" location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.email }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
              <span v-else class="font-weight-bold">{{ item.payerUser?.email }}</span>
            </div>
          </template>

          <template v-slot:item.payerUser.firstname="{ item }">
            <div>
              <v-tooltip v-if="item.payerUser?.id" location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">
                    {{ item.payerUser?.firstname }} {{ item.payerUser?.lastname }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
              <span v-else class="font-weight-bold">
                {{ item.payerUser?.firstname }} {{ item.payerUser?.lastname }}
              </span>
            </div>
          </template>

          <template v-slot:item.subscriptionType="{ item }">
            <div class="d-flex align-center ga-2">
              <strong v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Stripe'"
                      class="text-deep-purple-accent-3">Stripe</strong>
              <strong v-else class="text-amber-accent-4">Enterprise</strong>
              <v-btn v-if="item.id && !item.id.startsWith('ecus_')" density="compact" variant="plain" icon="mdi-information-outline"
                     @click="getStripeSubscriptionInfo(item)"
                     :loading="loadingGetStripeSubscriptionInfoUserId === item.id"
                     color="#0D66D0"
              >
              </v-btn>
            </div>
          </template>

          <template v-slot:item.updatedAt="{ item }">
            <div>
              {{ formatDate(item.updatedAt) }}
            </div>
          </template>

          <template v-slot:item.createdAt="{ item }">
            <div>
              {{ formatDate(item.createdAt) }}
            </div>
          </template>

          <template v-slot:item.feedback="{ item }">
            <v-btn
              v-if="item.feedback"
              icon="mdi-message-text-outline"
              variant="plain"
              color="primary"
              @click="openFeedbackDialog(item.feedback)"
              title="View Feedback"
            ></v-btn>
          </template>

          <template v-slot:expanded-row="{ item }">
            <td colspan="6">
              <div class="pa-5">
                <h4 class="mb-3">
                  {{ item.payerUser?.firstname && item.payerUser?.lastname ? item.payerUser.firstname : item.payerUser?.email }}'s subscription members
                </h4>
                <v-data-table
                  :headers="headersMembers"
                  :items="item.children"
                  :items-per-page="-1"
                  hover
                  hide-default-footer
                  class="elevation-1"
                >
                  <template v-slot:item.subscriptionType="{ item: childItem }">
                    <div class="d-flex align-center ga-2">
                      <div>
                        <strong v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Stripe'"
                                class="text-deep-purple-accent-3">Stripe</strong>
                        <strong v-else class="text-amber-accent-4">Enterprise</strong>
                      </div>
                      <v-tooltip
                        location="top"
                        v-if="getSubscriptionType(item.payerUser?.stripeCustomerId) === 'Enterprise'"
                      >
                        <template v-slot:activator="{ props }">
                          <div
                            v-bind="props"
                            class="action-button cursor-pointer"
                            @click="removeEnterpriseMember(childItem.email, item.payerUser?.id)"
                          >
                            <v-icon size="small" color="red">mdi-delete</v-icon>
                          </div>
                        </template>
                        <div>
                          <p>Remove member</p>
                        </div>
                      </v-tooltip>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </td>
          </template>

          <template v-slot:no-data>
            <v-alert v-if="normalizedSearch" color="error" icon="mdi-alert">
              Your search for "{{ normalizedSearch }}" found no results.
            </v-alert>
            <div v-else>
              No data available
            </div>
          </template>
        </v-data-table>
      </v-window>
    </v-card>

    <admin-subscription-info-dialog
      :is-visible="!!subscriptionInfo"
      :subscriptionInfo="subscriptionInfo"
      @close="subscriptionInfo = null"
    />

    <v-dialog v-model="showFeedbackDialog" max-width="480">
      <v-card class="rounded-lg" elevation="8">
        <v-card-title class="d-flex align-center" style="font-size: 16px; font-weight: 600;">
          <v-icon color="primary" class="mr-2">mdi-message-text-outline</v-icon>
          Feedback
          <v-spacer />
          <v-btn icon variant="text" size="16" @click="showFeedbackDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-5 px-6" style="font-size: 15px;">
          <div v-if="parsedFeedback">
            <div v-if="parsedFeedback.reasons && parsedFeedback.reasons.length" class="mb-3">
              <span class="font-weight-medium" style="color: var(--dark-3);">Reasons:</span>
              <span class="ml-1" style="color: var(--blue-3); font-weight: 500;">
                {{ parsedFeedback.reasons.join(', ') }}
              </span>
            </div>
            <div v-if="parsedFeedback.message">
              <span class="font-weight-medium" style="color: var(--dark-3);">Message:</span>
              <div class="mt-1" style="white-space: pre-line; color: var(--dark-4); background: var(--light-2); border-radius: 6px; padding: 10px 12px;">
                {{ parsedFeedback.message }}
              </div>
            </div>
          </div>
          <div v-else>
            <pre style="background: var(--light-2); border-radius: 6px; padding: 10px 12px;">{{ feedbackDialogContent }}</pre>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="justify-end px-4 py-2">
          <v-btn variant="text" color="primary" @click="showFeedbackDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import format from 'date-fns/format'
import { ENTERPRISE_CUSTOMER_ID_PREFIX } from '@/utils/constants'
import AdminSubscriptionInfoDialog from '@/components/AdminSubscriptionInfoDialog.vue'

export default {
  components: { AdminSubscriptionInfoDialog },
  data: () => ({
    tab: 'list-subscriptions',
    sortBy: [{ key: 'createdAt', order: 'desc' }],
    dialog: [],
    search: '',
    expanded: [],
    headersSubscriptions: [
      {
        title: 'CreatedAt',
        key: 'createdAt',
        sortable: true
      },
      {
        title: 'UpdatedAt',
        key: 'updatedAt',
        sortable: true
      },
      {
        title: 'Payer user email',
        sortable: false,
        key: 'payerUser.email'
      },
      {
        title: 'Payer user name',
        sortable: false,
        key: 'payerUser.firstname'
      },
      {
        title: 'Subscription Type',
        key: 'subscriptionType',
        sortable: false
      },
      {
        title: 'Feedback',
        key: 'feedback',
        sortable: false
      },
      { title: '', key: 'data-table-expand' }
    ],
    headersMembers: [
      {
        title: 'Email',
        sortable: false,
        key: 'email',
        width: '50%'
      },
      {
        title: 'Subscription Type',
        key: 'subscriptionType',
        sortable: false,
        width: '50%'
      }
    ],
    subscriptionInfo: null,
    loadingGetStripeSubscriptionInfoUserId: null,
    showFeedbackDialog: false,
    feedbackDialogContent: '',
    parsedFeedback: null
  }),
  computed: {
    ...mapState({
      subscriptions: state => state.subscriptions.subscriptions,
      loadingSubscriptions: state => state.subscriptions.loadingSubscriptions,
      enterpriseCustomers: state => state.enterpriseCustomers.enterpriseCustomers
    }),
    enterpriseCustomersByIdMap () {
      return (this.enterpriseCustomers || []).reduce((map, ec) => {
        map[ec.id] = ec
        return map
      }, {})
    },
    groupedSubscriptions () {
      const grouped = this.subscriptions.reduce((acc, subscription) => {
        const payerId = subscription.payerUser?.id || subscription.enterpriseCustomerId || `pending_${subscription.email}`

        if (!acc[payerId]) {
          const ec = subscription.enterpriseCustomerId
            ? this.enterpriseCustomersByIdMap[subscription.enterpriseCustomerId]
            : null
          const payerEmail = subscription.payerUser?.email || ec?.payerUserEmail || ec?.payerUser?.email || subscription.email

          acc[payerId] = {
            id: subscription.payerUser?.id || subscription.enterpriseCustomerId || null,
            payerUser: subscription.payerUser ||
              { email: payerEmail, firstname: null, lastname: null, stripeCustomerId: subscription.enterpriseCustomerId || 'ecus_pending' },
            createdAt: subscription.createdAt,
            updatedAt: subscription.updatedAt,
            feedback: subscription.feedback,
            children: []
          }
        }

        const payerEmail = acc[payerId].payerUser.email
        const isParent = subscription.email === payerEmail

        if (!isParent) {
          acc[payerId].children.push({ email: subscription.email })
        }

        return acc
      }, {})

      return Object.values(grouped)
    },
    normalizedSearch () {
      return this.search.trim()
    },
    filteredSubscriptions () {
      const query = this.normalizedSearch.toLowerCase()
      if (!query) {
        return this.groupedSubscriptions
      }

      return this.groupedSubscriptions.filter((row) => {
        const payer = row.payerUser || {}
        const firstname = (payer.firstname || '').toLowerCase()
        const lastname = (payer.lastname || '').toLowerCase()
        const fullName = `${firstname} ${lastname}`.trim()

        if ((payer.email || '').toLowerCase().includes(query)) {
          return true
        }
        if (firstname.includes(query) || lastname.includes(query) || fullName.includes(query)) {
          return true
        }

        return (row.children || []).some((child) => (child.email || '').toLowerCase().includes(query))
      })
    }
  },
  methods: {
    formatDate (date) {
      return format(new Date(date), 'MMMM d, yyyy HH:mm')
    },
    getSubscriptionType (customerId) {
      if (customerId.startsWith(ENTERPRISE_CUSTOMER_ID_PREFIX)) {
        return 'Enterprise'
      } else if (customerId.startsWith('cus_')) {
        return 'Stripe'
      }
      return null
    },
    async removeEnterpriseMember (email, payerUserId) {
      const response = await this.$store.dispatch('enterpriseCustomers/removeEnterpriseMemberByAdmin', { email, payerUserId })
      if (response?.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Member has been removed.' }, { root: true })
      } else if (response) {
        this.$store.commit('snackbar/showMessage', {
          content: response.message || 'Failed to remove member.',
          color: 'red'
        }, { root: true })
      }
      this.$store.dispatch('subscriptions/listSubscriptions')
    },
    async getStripeSubscriptionInfo (user) {
      this.loadingGetStripeSubscriptionInfoUserId = user.id
      const response = await this.$store.dispatch('subscriptions/getStripeSubscriptionInfo', { userId: user.id })
      this.loadingGetStripeSubscriptionInfoUserId = null
      if (response.success) {
        this.subscriptionInfo = {
          user,
          info: response
        }
      } else {
        if (response.message === 'No active subscription found.') {
          this.subscriptionInfo = {
            user,
            info: {
              subscriptionDetails: {
                status: 'canceled'
              }
            }
          }
        } else {
          this.$store.commit('snackbar/showMessage', {
            content: response.message,
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
        }
      }
    },
    openFeedbackDialog (feedback) {
      this.feedbackDialogContent = feedback
      try {
        this.parsedFeedback = JSON.parse(feedback)
      } catch (e) {
        this.parsedFeedback = null
      }
      this.showFeedbackDialog = true
    }
  },
  mounted () {
    this.$store.dispatch('subscriptions/listSubscriptions')
    this.$store.dispatch('enterpriseCustomers/listEnterpriseCustomers')
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  background: var(--light-1);
  border: 1px solid var(--light-1);
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
